<template>
    <div class="fac-chart">
        <div class="fac-chart__header">
            <div class="fac-chart__heading min-w-0">
                <p class="fac-chart__eyebrow">Inventory mix</p>
                <div class="fac-chart__title-row">
                    <h3 class="fac-chart__title">Facility by category</h3>
                    <div class="fac-chart__tip" tabindex="0">
                        <span class="fac-chart__tip-btn" aria-label="How categories are calculated">
                            <font-awesome-icon :icon="['fas', 'info-circle']" />
                        </span>
                        <div class="fac-chart__tooltip" role="tooltip">
                            <p class="fac-chart__tooltip-title">How categories are calculated</p>
                            <p class="fac-chart__tooltip-intro">
                                Each slice is the sum of record counts from these sections:
                            </p>
                            <ul class="fac-chart__tooltip-list">
                                <li v-for="item in categoryTips" :key="item.label">
                                    <span class="fac-chart__tooltip-dot" :style="{ background: item.color }"></span>
                                    <div class="fac-chart__tooltip-item">
                                        <strong>{{ item.label }}</strong>
                                        <span>{{ item.detail }}</span>
                                    </div>
                                </li>
                            </ul>
                            <p class="fac-chart__tooltip-note">Building Overview is not included.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!hasData" class="fac-chart__empty">No category data available yet.</div>
        <apexchart
            v-else
            type="donut"
            height="320"
            :options="chartOptions"
            :series="series"
        />
    </div>
</template>

<script>
export default {
    name: "FacilityCategoryMixChart",
    props: {
        data: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    data() {
        return {
            series: [],
            categoryTips: [
                {
                    label: "Teaching",
                    color: "#b45309",
                    detail: "Classrooms + Labs + Auditorium + Meeting Rooms",
                },
                {
                    label: "Offices",
                    color: "#0f766e",
                    detail: "Academic Offices + Admin Offices",
                },
                {
                    label: "Support",
                    color: "#57534e",
                    detail: "Gates + Toilets + Stores + Service Rooms",
                },
                {
                    label: "Amenities",
                    color: "#ca8a04",
                    detail: "Sport Facilities + Canteen + Clinic + Prayer Room + Pantries & Kitchen + Vending Machines",
                },
                {
                    label: "Grounds & Security",
                    color: "#78716c",
                    detail: "Car Parking + Palm & Trees + Security Camera",
                },
            ],
            chartOptions: {
                chart: {
                    type: "donut",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, speed: 450 },
                },
                labels: [],
                colors: ["#b45309", "#0f766e", "#57534e", "#ca8a04", "#78716c"],
                legend: {
                    position: "bottom",
                    fontSize: "12px",
                    markers: { radius: 10 },
                    itemMargin: { horizontal: 8, vertical: 4 },
                    formatter: function (val, opts) {
                        return (
                            val +
                            " · " +
                            opts.w.globals.series[opts.seriesIndex]
                        );
                    },
                },
                dataLabels: {
                    enabled: true,
                    style: { fontSize: "11px", fontWeight: 600 },
                    dropShadow: { enabled: false },
                    formatter: function (val) {
                        return Math.round(val) + "%";
                    },
                },
                stroke: { width: 2, colors: ["#ffffff"] },
                plotOptions: {
                    pie: {
                        donut: {
                            size: "68%",
                            labels: {
                                show: true,
                                name: {
                                    show: true,
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#78716c",
                                },
                                value: {
                                    show: true,
                                    fontSize: "22px",
                                    fontWeight: 800,
                                    color: "#1c1917",
                                },
                                total: {
                                    show: true,
                                    label: "Spaces",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#78716c",
                                    formatter: function (w) {
                                        var total = w.globals.seriesTotals.reduce(
                                            function (a, b) {
                                                return a + b;
                                            },
                                            0
                                        );
                                        return total;
                                    },
                                },
                            },
                        },
                    },
                },
                tooltip: {
                    custom: function () {
                        return "";
                    },
                },
                responsive: [
                    {
                        breakpoint: 640,
                        options: {
                            legend: { position: "bottom" },
                        },
                    },
                ],
            },
        };
    },
    computed: {
        hasData() {
            return (
                Array.isArray(this.data) &&
                this.data.some(function (item) {
                    return item && item.value > 0;
                })
            );
        },
    },
    methods: {
        buildSliceTooltip(filtered) {
            var colors = this.chartOptions.colors || [];
            return function (opts) {
                var seriesIndex = opts.seriesIndex;
                var item = filtered[seriesIndex];
                if (!item) return "";

                var value = opts.series[seriesIndex] || 0;
                var total = 0;
                for (var i = 0; i < opts.series.length; i++) {
                    total += opts.series[i] || 0;
                }
                var percent =
                    total > 0 ? Math.round((value / total) * 100) : 0;
                var color = colors[seriesIndex] || "#b45309";
                var sections = item.sections || [];

                var rowsHtml = "";
                for (var s = 0; s < sections.length; s++) {
                    var section = sections[s];
                    rowsHtml +=
                        '<div style="display:flex;justify-content:space-between;gap:1rem;padding:0.2rem 0;">' +
                        '<span style="color:#a8a29e;font-size:0.72rem;">' +
                        section.label +
                        "</span>" +
                        '<span style="color:#fafaf9;font-size:0.72rem;font-weight:700;">' +
                        section.count +
                        "</span>" +
                        "</div>";
                }

                return (
                    '<div style="min-width:12.5rem;padding:0.75rem 0.85rem;border-radius:0.75rem;background:linear-gradient(165deg,#1c1917 0%,#292524 100%);box-shadow:0 12px 28px rgba(28,25,23,0.28);">' +
                    '<div style="display:flex;align-items:center;gap:0.45rem;margin-bottom:0.55rem;">' +
                    '<span style="width:0.55rem;height:0.55rem;border-radius:999px;background:' +
                    color +
                    ';"></span>' +
                    '<span style="font-size:0.8rem;font-weight:700;color:#fafaf9;">' +
                    item.label +
                    "</span>" +
                    "</div>" +
                    '<div style="border-top:1px solid rgba(168,162,158,0.22);padding-top:0.4rem;margin-bottom:0.45rem;">' +
                    rowsHtml +
                    "</div>" +
                    '<div style="border-top:1px solid rgba(168,162,158,0.22);padding-top:0.45rem;display:flex;flex-direction:column;gap:0.25rem;">' +
                    '<div style="display:flex;justify-content:space-between;gap:1rem;">' +
                    '<span style="font-size:0.7rem;color:#d6d3d1;">Total</span>' +
                    '<span style="font-size:0.75rem;font-weight:800;color:#fbbf24;">' +
                    value +
                    "</span>" +
                    "</div>" +
                    '<div style="font-size:0.68rem;line-height:1.4;color:#a8a29e;">' +
                    value +
                    " ÷ " +
                    total +
                    " × 100 = " +
                    percent +
                    "%" +
                    "</div>" +
                    "</div>" +
                    "</div>"
                );
            };
        },
    },
    watch: {
        data: {
            handler: function (rows) {
                if (!Array.isArray(rows)) return;
                var filtered = rows.filter(function (item) {
                    return item && item.value > 0;
                });
                this.series = filtered.map(function (item) {
                    return item.value;
                });
                this.chartOptions = Object.assign({}, this.chartOptions, {
                    labels: filtered.map(function (item) {
                        return item.label;
                    }),
                    tooltip: {
                        custom: this.buildSliceTooltip(filtered),
                    },
                });
            },
            immediate: true,
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-chart {
    position: relative;
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 1rem;
    padding: 1.25rem 1.35rem 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    height: 100%;
}

.fac-chart__header {
    margin-bottom: 0.35rem;
}

.fac-chart__eyebrow {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-chart__title-row {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.2rem;
}

.fac-chart__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1c1917;
}

.fac-chart__tip {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
}

.fac-chart__tip-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 999px;
    color: #d6d3d1;
    font-size: 0.78rem;
    cursor: help;
    transition: color 0.15s ease, background 0.15s ease;
}

.fac-chart__tip:hover .fac-chart__tip-btn,
.fac-chart__tip:focus-within .fac-chart__tip-btn {
    color: #b45309;
    background: #fff7ed;
}

.fac-chart__tooltip {
    position: absolute;
    top: calc(100% + 0.65rem);
    left: 0;
    z-index: 30;
    width: 19.5rem;
    max-width: calc(100vw - 3rem);
    padding: 0.85rem 0.95rem;
    border-radius: 0.85rem;
    background: linear-gradient(165deg, #1c1917 0%, #292524 100%);
    color: #fafaf9;
    box-shadow: 0 16px 36px rgba(28, 25, 23, 0.32);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(4px);
    transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;
}

.fac-chart__tooltip::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 0.55rem;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent #1c1917 transparent;
}

.fac-chart__tip:hover .fac-chart__tooltip,
.fac-chart__tip:focus-within .fac-chart__tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
}

.fac-chart__tooltip-title {
    margin: 0 0 0.35rem;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #fbbf24;
}

.fac-chart__tooltip-intro {
    margin: 0 0 0.7rem;
    font-size: 0.75rem;
    line-height: 1.45;
    color: #d6d3d1;
}

.fac-chart__tooltip-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}

.fac-chart__tooltip-list li {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
}

.fac-chart__tooltip-dot {
    flex-shrink: 0;
    width: 0.5rem;
    height: 0.5rem;
    margin-top: 0.3rem;
    border-radius: 999px;
}

.fac-chart__tooltip-item {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.fac-chart__tooltip-item strong {
    font-size: 0.78rem;
    font-weight: 700;
    color: #fafaf9;
}

.fac-chart__tooltip-item span {
    font-size: 0.72rem;
    line-height: 1.4;
    color: #a8a29e;
}

.fac-chart__tooltip-note {
    margin: 0.75rem 0 0;
    padding-top: 0.65rem;
    border-top: 1px solid rgba(168, 162, 158, 0.25);
    font-size: 0.7rem;
    line-height: 1.4;
    color: #a8a29e;
}

.fac-chart__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    color: #a8a29e;
    font-size: 0.9rem;
}
</style>
